import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { Users, Package, Store, UserCheck, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Activity, Boxes } from 'lucide-react';

const fmtCurrency = (v) => `\u20B9${(v || 0).toLocaleString('en-IN')}`;
const fmtCompact = (v) => {
  if (!v) return '\u20B90';
  if (v >= 10000000) return `\u20B9${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return `\u20B9${v.toLocaleString('en-IN')}`;
};

const CHART_COLORS = ['#0f172a', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
const PIE_COLORS = ['#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0'];

const StatCard = ({ label, value, icon: Icon, color, sub, trend }) => (
  <Card className="group hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color || 'text-slate-900'}`}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          {Icon && <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="w-5 h-5 text-slate-600" /></div>}
          {trend !== undefined && (
            <div className={`flex items-center gap-0.5 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }}></span>
          <span className="text-slate-300">{p.name}:</span>
          <span className="font-medium">{fmtCurrency(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const SuperAdminDashboard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockTrend, setStockTrend] = useState(null);
  const [stockRange, setStockRange] = useState('3m');

  useEffect(() => {
    const fetchKPIs = async () => {
      try {
        const res = await api.get('/dashboard/super-admin-kpis');
        setData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load dashboard');
      } finally { setLoading(false); }
    };
    fetchKPIs();
  }, []);

  useEffect(() => {
    const fetchTrend = async () => {
      try {
        const res = await api.get(`/snapshots/trend?range=${stockRange}&granularity=daily`);
        setStockTrend(res.data);
      } catch { /* silent — trend is optional */ }
    };
    fetchTrend();
  }, [stockRange]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-[80vh]">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-slate-800 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-slate-500">Loading business intelligence...</p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center text-slate-400">No data available</div>;

  const { revenue, expenses, quick_stats, monthly_trend, top_agents, top_customers, closing_stock, recent_activity, fy_label } = data;
  const profitMargin = revenue.net_sales > 0 ? Math.round((revenue.gross_profit / revenue.net_sales) * 100) : 0;
  const expenseRatio = revenue.net_sales > 0 ? Math.round((expenses.total / revenue.net_sales) * 100) : 0;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Nunito' }}>Business Overview</h1>
          <p className="text-slate-500 mt-1">{fy_label} Financial Year Dashboard</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-slate-100 text-slate-700 text-xs px-3 py-1">{fy_label}</Badge>
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Activity className="w-3.5 h-3.5" />
            <span>{recent_activity.se_last_7d} SE, {recent_activity.pe_last_7d} PE, {recent_activity.sr_last_7d} SR this week</span>
          </div>
        </div>
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
        <StatCard label="Net Sales" value={fmtCompact(revenue.net_sales)} icon={TrendingUp} color="text-emerald-700" sub={`${revenue.se_count} entries - ${revenue.sr_count} returns`} />
        <StatCard label="Invoice Sales" value={fmtCompact(revenue.invoice_sales)} color="text-blue-700" sub={`Challan: ${fmtCompact(revenue.challan_sales)}`} />
        <StatCard label="Purchases" value={fmtCompact(revenue.total_purchases)} icon={TrendingDown} color="text-red-600" sub={`${revenue.pe_count} entries`} />
        <StatCard label="Gross Profit" value={fmtCompact(revenue.gross_profit)} color={revenue.gross_profit >= 0 ? 'text-emerald-700' : 'text-red-600'} sub={`${profitMargin}% margin`} />
        <StatCard label="Expenses" value={fmtCompact(expenses.total)} color="text-amber-700" sub={`${expenseRatio}% of net sales`} />
        <StatCard label="Closing Stock" value={fmtCompact(closing_stock)} color="text-slate-700" sub={`${quick_stats.total_skus} SKUs`} />
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-slate-50 to-white"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center"><Users className="w-4 h-4 text-blue-600" /></div>
          <div><p className="text-xs text-slate-500">Customers</p><p className="text-lg font-bold text-slate-800">{quick_stats.total_customers}</p></div>
        </CardContent></Card>
        <Card className="bg-gradient-to-br from-slate-50 to-white"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center"><Store className="w-4 h-4 text-emerald-600" /></div>
          <div><p className="text-xs text-slate-500">Vendors</p><p className="text-lg font-bold text-slate-800">{quick_stats.total_vendors}</p></div>
        </CardContent></Card>
        <Card className="bg-gradient-to-br from-slate-50 to-white"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center"><Package className="w-4 h-4 text-purple-600" /></div>
          <div><p className="text-xs text-slate-500">Products</p><p className="text-lg font-bold text-slate-800">{quick_stats.total_products}</p></div>
        </CardContent></Card>
        <Card className="bg-gradient-to-br from-slate-50 to-white"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center"><UserCheck className="w-4 h-4 text-amber-600" /></div>
          <div><p className="text-xs text-slate-500">Agents</p><p className="text-lg font-bold text-slate-800">{quick_stats.total_agents}</p></div>
        </CardContent></Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Monthly Revenue Trend */}
        <Card className="xl:col-span-2">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Monthly Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthly_trend}>
                <defs>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="purchGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="net_sales" name="Net Sales" stroke="#3b82f6" strokeWidth={2} fill="url(#salesGrad)" />
                <Area type="monotone" dataKey="purchases" name="Purchases" stroke="#ef4444" strokeWidth={1.5} fill="url(#purchGrad)" strokeDasharray="5 5" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Expense Breakdown */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Expense Breakdown</h3>
            {expenses.by_category?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={expenses.by_category} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="amount" paddingAngle={2}>
                      {expenses.by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {expenses.by_category.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}></span>
                        <span className="text-slate-600">{c.category}</span>
                      </div>
                      <span className="font-medium text-slate-800 font-mono">{fmtCompact(c.amount)}</span>
                    </div>
                  ))}
                  {expenses.freight_charges > 0 && (
                    <div className="flex items-center justify-between text-xs pt-1 border-t">
                      <span className="text-slate-500">+ Freight Charges</span>
                      <span className="font-medium text-slate-700 font-mono">{fmtCompact(expenses.freight_charges)}</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">No expense data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stock Value Trend */}
      <Card data-testid="stock-trend-card">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Boxes className="w-5 h-5 text-emerald-700" /></div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">Inventory Value Trend</h3>
                <p className="text-xs text-slate-500">Daily closing stock value at average cost</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {stockTrend?.mom?.delta_pct != null && (
                <div className="text-right" data-testid="trend-mom">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">MoM</p>
                  <p className={`text-sm font-bold flex items-center gap-1 ${stockTrend.mom.delta_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {stockTrend.mom.delta_pct >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                    {Math.abs(stockTrend.mom.delta_pct)}%
                  </p>
                </div>
              )}
              {stockTrend?.yoy?.delta_pct != null && (
                <div className="text-right" data-testid="trend-yoy">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">YoY</p>
                  <p className={`text-sm font-bold flex items-center gap-1 ${stockTrend.yoy.delta_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {stockTrend.yoy.delta_pct >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                    {Math.abs(stockTrend.yoy.delta_pct)}%
                  </p>
                </div>
              )}
              <div className="flex gap-1">
                {[
                  { key: '1m', label: '1M' },
                  { key: '3m', label: '3M' },
                  { key: '6m', label: '6M' },
                  { key: '1y', label: '1Y' },
                  { key: 'all', label: 'All' },
                ].map(r => (
                  <button
                    key={r.key}
                    data-testid={`trend-range-${r.key}`}
                    onClick={() => setStockRange(r.key)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${stockRange === r.key ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {stockTrend?.points?.length > 0 ? (
            <>
              <div className="flex items-baseline gap-3 mb-2">
                <p className="text-3xl font-bold text-slate-900" data-testid="trend-latest-value">{fmtCurrency(stockTrend.latest?.value)}</p>
                <p className="text-xs text-slate-500">as on {stockTrend.latest?.date} • {stockTrend.latest?.qty?.toLocaleString('en-IN')} units</p>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={stockTrend.points}>
                  <defs>
                    <linearGradient id="stockGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#94a3b8' }} minTickGap={30} />
                  <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip
                    formatter={(v) => [fmtCurrency(v), 'Stock Value']}
                    labelFormatter={(l) => `Date: ${l}`}
                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="value" name="Stock Value" stroke="#10b981" strokeWidth={2} fill="url(#stockGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm" data-testid="trend-empty">
              No snapshot data yet — open Financial Statements and click "Rebuild Stock History"
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Agents & Customers */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Top Agents */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Top Agents by Sales</h3>
            {top_agents.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={top_agents.slice(0, 7)} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtCompact} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="sales" name="Sales" fill="#0f172a" radius={[0, 4, 4, 0]} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5">
                  {top_agents.slice(0, 5).map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">{i + 1}</span>
                        <span className="text-slate-700 font-medium">{a.name}</span>
                        {a.city && <span className="text-slate-400">{a.city}</span>}
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-slate-800 font-mono">{fmtCompact(a.sales)}</span>
                        <span className="text-slate-400 ml-1">({a.entries} SE)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">No agent data</div>
            )}
          </CardContent>
        </Card>

        {/* Top Customers */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Top Customers by Sales</h3>
            {top_customers.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={top_customers.slice(0, 7)} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtCompact} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="sales" name="Sales" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={16} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5">
                  {top_customers.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-600">{i + 1}</span>
                        <span className="text-slate-700 font-medium">{c.name}</span>
                        {c.city && <span className="text-slate-400">{c.city}</span>}
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-blue-700 font-mono">{fmtCompact(c.sales)}</span>
                        <span className="text-slate-400 ml-1">({c.entries} SE)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-slate-400 text-sm">No customer data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Income vs Expense Visual */}
      <Card>
        <CardContent className="p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Income vs Expense Ratio</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Visual bar */}
            <div className="md:col-span-2">
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">Net Sales</span>
                    <span className="font-bold text-slate-800">{fmtCurrency(revenue.net_sales)}</span>
                  </div>
                  <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: '100%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">Purchases</span>
                    <span className="font-bold text-slate-800">{fmtCurrency(revenue.total_purchases)}</span>
                  </div>
                  <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-red-400 rounded-full transition-all duration-1000" style={{ width: `${revenue.net_sales > 0 ? Math.min((revenue.total_purchases / revenue.net_sales) * 100, 100) : 0}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">Expenses</span>
                    <span className="font-bold text-slate-800">{fmtCurrency(expenses.total)}</span>
                  </div>
                  <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full transition-all duration-1000" style={{ width: `${revenue.net_sales > 0 ? Math.min((expenses.total / revenue.net_sales) * 100, 100) : 0}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500 font-medium">Gross Profit</span>
                    <span className={`font-bold ${revenue.gross_profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtCurrency(revenue.gross_profit)}</span>
                  </div>
                  <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-1000 ${revenue.gross_profit >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                      style={{ width: `${revenue.net_sales > 0 ? Math.min(Math.abs(revenue.gross_profit) / revenue.net_sales * 100, 100) : 0}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
            {/* Summary */}
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Profit Margin</p>
                <p className={`text-2xl font-bold ${profitMargin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{profitMargin}%</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Expense Ratio</p>
                <p className="text-2xl font-bold text-amber-700">{expenseRatio}%</p>
                <p className="text-[10px] text-slate-400">of net sales</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Closing Stock</p>
                <p className="text-2xl font-bold text-slate-700">{fmtCompact(closing_stock)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
